import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

// Preset options
const PRESETS = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '60', label: 'Últimos 60 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: 'custom', label: 'Período personalizado' },
];

export const DATE_RANGE_PRESETS = PRESETS;

export const DateRangePicker = ({ 
  mode = 'preset', 
  preset = '30',
  startDate,
  endDate,
  onChange,
  disabled = false,
  presets = PRESETS
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState({ from: undefined, to: undefined });
  const [error, setError] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  // Get display label based on current mode
  // Parse dates correctly to avoid UTC offset issues
  const getDisplayLabel = () => {
    if (mode === 'custom' && startDate && endDate) {
      // Parse YYYY-MM-DD as local date
      const parseDateParts = (dateStr) => {
        const parts = dateStr.split('-');
        return { day: parts[2], month: parts[1], year: parts[0] };
      };
      const start = parseDateParts(startDate);
      const end = parseDateParts(endDate);
      return `${start.day}/${start.month}/${start.year} – ${end.day}/${end.month}/${end.year}`;
    }
    const presetOption = presets.find(p => p.value === preset);
    return presetOption?.label || 'Últimos 30 dias';
  };

  const handlePresetSelect = (value) => {
    setError(null);
    if (value === 'custom') {
      setShowCalendar(true);
      // Initialize temp range with current custom dates or last 7 days
      if (startDate && endDate) {
        // Parse YYYY-MM-DD as local date to avoid UTC offset
        const parseLocalDate = (dateStr) => {
          const parts = dateStr.split('-');
          return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        };
        setTempRange({ from: parseLocalDate(startDate), to: parseLocalDate(endDate) });
      } else {
        const today = new Date();
        const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        start.setDate(start.getDate() - 7);
        setTempRange({ from: start, to: end });
      }
    } else {
      setShowCalendar(false);
      onChange({ mode: 'preset', preset: value });
      setIsOpen(false);
    }
  };

  const handleDateRangeSelect = (range) => {
    setError(null);
    setTempRange(range || { from: undefined, to: undefined });
  };

  const handleConfirmCustomRange = () => {
    if (!tempRange?.from || !tempRange?.to) {
      setError('Selecione as datas inicial e final.');
      return;
    }

    // Validate range
    if (tempRange.from > tempRange.to) {
      setError('Período inválido: a data inicial não pode ser maior que a data final.');
      return;
    }

    // Check for max range (optional - GA4 limit is typically 1 year)
    const diffTime = Math.abs(tempRange.to - tempRange.from);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 365) {
      setError('O período máximo permitido é de 365 dias.');
      return;
    }

    onChange({
      mode: 'custom',
      startDate: format(tempRange.from, 'yyyy-MM-dd'),
      endDate: format(tempRange.to, 'yyyy-MM-dd'),
    });
    setIsOpen(false);
    setShowCalendar(false);
    setError(null);
  };

  const handleCancel = () => {
    setShowCalendar(false);
    setError(null);
    setTempRange({ from: undefined, to: undefined });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "min-w-[200px] justify-between text-left font-normal",
            mode === 'custom' && "min-w-[260px]"
          )}
        >
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{getDisplayLabel()}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        {!showCalendar ? (
          // Preset options menu
          <div className="p-2">
            {presets.map((option) => (
              <button
                key={option.value}
                onClick={() => handlePresetSelect(option.value)}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm rounded-md hover:bg-accent transition-colors",
                  (mode === 'preset' && preset === option.value) || (mode === 'custom' && option.value === 'custom')
                    ? "bg-accent font-medium"
                    : ""
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : (
          // Calendar view for custom range
          <div className="p-4 space-y-4">
            <div className="text-sm font-medium text-center">
              Selecione o período
            </div>
            
            <Calendar
              mode="range"
              selected={tempRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={2}
              locale={ptBR}
              disabled={(date) => date > new Date()}
            />
            
            {/* Selected range display */}
            <div className="text-sm text-center text-muted-foreground">
              {tempRange?.from ? (
                tempRange.to ? (
                  <>
                    {format(tempRange.from, 'dd/MM/yyyy', { locale: ptBR })} – {format(tempRange.to, 'dd/MM/yyyy', { locale: ptBR })}
                  </>
                ) : (
                  <>
                    {format(tempRange.from, 'dd/MM/yyyy', { locale: ptBR })} – Selecione a data final
                  </>
                )
              ) : (
                'Clique para selecionar a data inicial'
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="text-sm text-red-600 text-center bg-red-50 p-2 rounded">
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmCustomRange}
                disabled={!tempRange?.from || !tempRange?.to}
              >
                Aplicar
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
